import { useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Typography,
} from '@material-ui/core';
import {
    CheckCircleOutlined,
    CloudUploadOutlined,
    WarningRounded,
} from '@material-ui/icons';

const SUCCESS_DISPLAY_MS = 800;
const FADE_OUT_MS = 200;

const ContradictsResolutionModal = ({
    open = false,
    blockedItems = [],
    acceptedExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg', '.pjc'],
    onReplace,
    onCancel,
    onItemResolved,
}) => {

    const replaceInputRefs = useRef({});
    const [cancelingId, setCancelingId] = useState(null);
    const [replacingId, setReplacingId] = useState(null);
    const [successId, setSuccessId] = useState(null);
    const [fadingOutId, setFadingOutId] = useState(null);
    const [draggingId, setDraggingId] = useState(null);

    const triggerReplace = (stagingId) => {
        const ref = replaceInputRefs.current[stagingId];
        if (!ref) return;
        ref.value = '';
        ref.click();
    };

    const handleReplaceFile = async (stagingId, file) => {
        if (!file || replacingId || cancelingId) return;
        setReplacingId(stagingId);
        try {
            const result = typeof onReplace === 'function' ? await onReplace(stagingId, file) : null;
            if (result && result.success) {
                setReplacingId(null);
                setSuccessId(stagingId);
                await new Promise(r => setTimeout(r, SUCCESS_DISPLAY_MS));
                setFadingOutId(stagingId);
                await new Promise(r => setTimeout(r, FADE_OUT_MS));
                setSuccessId(null);
                setFadingOutId(null);
                if (typeof onItemResolved === 'function') onItemResolved(stagingId);
                return;
            }
        } finally {
            setReplacingId(null);
        }
    };

    const handleInputChange = (stagingId, event) => {
        const file = (event && event.target && event.target.files && event.target.files[0]) || null;
        handleReplaceFile(stagingId, file);
    };

    const handleDrop = (stagingId, event) => {
        event.preventDefault();
        event.stopPropagation();
        setDraggingId(null);
        const file = (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) || null;
        handleReplaceFile(stagingId, file);
    };

    const handleCancelClick = async (stagingId) => {
        if (cancelingId || replacingId) return;
        setCancelingId(stagingId);
        try {
            if (typeof onCancel === 'function') await onCancel(stagingId);
        } finally {
            setCancelingId(null);
        }
    };

    const renderDropzone = (item, anyBusy) => {
        const isDragging = draggingId === item.stagingId;
        return (
            <Box
                role="button"
                tabIndex={0}
                onClick={() => !anyBusy && triggerReplace(item.stagingId)}
                onKeyDown={(e) => {
                    if (anyBusy) return;
                    if (e.key === 'Enter' || e.key === ' ') triggerReplace(item.stagingId);
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    if (anyBusy) return;
                    setDraggingId(item.stagingId);
                }}
                onDragLeave={() => setDraggingId(prev => (prev === item.stagingId ? null : prev))}
                onDrop={(e) => !anyBusy && handleDrop(item.stagingId, e)}
                style={{
                    marginTop: 24,
                    border: `2px dashed ${isDragging ? '#376fd0' : 'rgba(0,0,0,0.16)'}`,
                    borderRadius: 12,
                    background: isDragging ? '#F5F8FF' : '#FAFAFA',
                    padding: '28px 16px',
                    textAlign: 'center',
                    cursor: anyBusy ? 'not-allowed' : 'pointer',
                    transition: 'border-color 180ms ease, background 180ms ease',
                    outline: 'none',
                }}
            >
                <CloudUploadOutlined style={{ fontSize: 32, color: '#376fd0', marginBottom: 8 }} />
                <Typography variant="body2" style={{ fontWeight: 500, color: 'rgba(0,0,0,0.78)', lineHeight: 1.45 }}>
                    Solte o arquivo correto aqui ou clique pra selecionar
                </Typography>
                <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.5)', display: 'block', marginTop: 6 }}>
                    PDF, Word, Excel, imagens (JPG, PNG) e PJC
                </Typography>
                <input
                    ref={(el) => { replaceInputRefs.current[item.stagingId] = el; }}
                    type="file"
                    accept={acceptedExtensions.join(',')}
                    onChange={(e) => handleInputChange(item.stagingId, e)}
                    style={{ display: 'none' }}
                />
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            disableBackdropClick
            disableEscapeKeyDown
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: 16,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                },
            }}
            transitionDuration={{ enter: 280, exit: 220 }}
        >
            <DialogTitle disableTypography style={{ padding: '32px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <Box display="flex" alignItems="flex-start" style={{ gap: 16 }}>
                    <Box
                        style={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            background: '#FFF1F1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <WarningRounded style={{ color: '#D70015', fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" style={{ fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }}>
                            {blockedItems.length === 1
                                ? 'Documento não corresponde à solicitação'
                                : `${blockedItems.length} documentos não correspondem à solicitação`}
                        </Typography>
                        <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.65)', marginTop: 6, lineHeight: 1.5 }}>
                            Para concluir a solicitação, substitua {blockedItems.length === 1 ? 'o arquivo abaixo' : 'cada arquivo abaixo'} pelo correspondente ao cadastro da solicitação.
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent style={{ padding: 0 }}>
                {blockedItems.map((item, idx) => {
                    const justification = item && item.classification && item.classification.justification;
                    const isReplacing = replacingId === item.stagingId;
                    const isCanceling = cancelingId === item.stagingId;
                    const isSuccess = successId === item.stagingId;
                    const isFadingOut = fadingOutId === item.stagingId;
                    const anyBusy = replacingId !== null || cancelingId !== null || successId !== null;
                    return (
                        <Box
                            key={item.stagingId || `blocked-${idx}`}
                            style={{
                                opacity: isFadingOut ? 0 : 1,
                                transition: `opacity ${FADE_OUT_MS}ms ease`,
                            }}
                        >
                            <Box style={{ padding: '32px' }}>
                                <Typography
                                    variant="body1"
                                    style={{
                                        fontWeight: 500,
                                        fontSize: 16,
                                        color: '#1A1A1A',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                    title={item.name}
                                >
                                    {item.name}
                                </Typography>

                                {isSuccess ? (
                                    <Box display="flex" alignItems="center" style={{ gap: 10, marginTop: 16 }}>
                                        <CheckCircleOutlined style={{ color: '#009700', fontSize: 18 }} />
                                        <Typography variant="body2" style={{ color: '#009700', fontWeight: 500, lineHeight: 1.5, fontSize: 14 }}>
                                            Documento aceito
                                        </Typography>
                                    </Box>
                                ) : isReplacing ? (
                                    <Box display="flex" alignItems="center" style={{ gap: 10, marginTop: 16 }}>
                                        <CircularProgress size={14} thickness={5} style={{ color: '#376fd0' }} />
                                        <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.65)', lineHeight: 1.5, fontSize: 14 }}>
                                            Analisando substituto…
                                        </Typography>
                                    </Box>
                                ) : (
                                    justification && (
                                        <Typography
                                            variant="body2"
                                            style={{
                                                color: 'rgba(0,0,0,0.65)',
                                                marginTop: 12,
                                                lineHeight: 1.5,
                                                fontSize: 14,
                                            }}
                                        >
                                            {justification}
                                        </Typography>
                                    )
                                )}

                                {!isSuccess && !isReplacing && renderDropzone(item, anyBusy)}

                                {!isSuccess && !isReplacing && (
                                    <Box display="flex" justifyContent="center" style={{ marginTop: 16 }}>
                                        <Button
                                            onClick={() => handleCancelClick(item.stagingId)}
                                            disabled={anyBusy}
                                            startIcon={isCanceling
                                                ? <CircularProgress size={14} thickness={5} style={{ color: 'rgba(0,0,0,0.4)' }} />
                                                : null}
                                            style={{
                                                textTransform: 'none',
                                                color: 'rgba(0,0,0,0.55)',
                                                fontSize: 13,
                                                fontWeight: 500,
                                            }}
                                        >
                                            Cancelar envio deste arquivo
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                            {idx < blockedItems.length - 1 && <Divider />}
                        </Box>
                    );
                })}
            </DialogContent>
        </Dialog>
    );
};

export default ContradictsResolutionModal;
